import { DocumentLead, DocumentProvenance } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { openHostedDocumentPdf } from '@/hooks/api/part-finder-reports/useGetDocumentPreview';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { Box, Link } from '@mui/material';

function actionLabel(url: string): string {
  return /\.pdf(\?|#|$)/i.test(url) ? 'View PDF' : 'Open document';
}

export function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const HostedPdfLink = ({ reportId, index }: { reportId: string; index: number }) => {
  const { getTenantId } = useTenants();
  const { showToast } = useToast();
  return (
    <Link
      component='button'
      variant='body2'
      onClick={() => {
        openHostedDocumentPdf(getTenantId(), reportId, index).catch(() => {
          showToast({ message: 'Failed to open the hosted PDF', type: 'error' });
        });
      }}
    >
      View PDF
    </Link>
  );
};

export const DocumentActionLinks = ({
  lead,
  reportId,
  index,
}: {
  lead: DocumentLead;
  reportId: string;
  index: number;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
    {lead.hasHostedPdf && <HostedPdfLink reportId={reportId} index={index} />}
    {!lead.hasHostedPdf && (
      <Link href={lead.pdfUrl ?? lead.url} target='_blank' rel='noopener noreferrer' variant='body2'>
        {lead.pdfUrl ? 'View PDF' : actionLabel(lead.url)}
      </Link>
    )}
    {(lead.pdfUrl ?? lead.hasHostedPdf) && (
      <Link href={lead.url} target='_blank' rel='noopener noreferrer' variant='body2'>
        {lead.provenance === DocumentProvenance.PublicDomain ? 'View on ASSIST' : 'View source'}
      </Link>
    )}
    {lead.hasHostedPdf && lead.provenance !== DocumentProvenance.PublicDomain && (
      <BodyText color='text.secondary'>Hosted copy © {sourceHost(lead.url)}</BodyText>
    )}
  </Box>
);
